import { ShowColumnsFilter } from "@/components/ColumnMenu";
import { MIN_RESIZABLE_WIDTH_KENDO_TABLE } from "@/utils/kendo";

export interface columnInterface {
  title?: string;
  field?: string;
  show?: boolean;
  filter?: "boolean" | "numeric" | "text" | "date" | undefined;
  minWidth?: number;
  minGridWidth?: number;
  locked?: boolean;
  width?: string | number;
  columnMenu?: any;
  minResizableWidth?: number;
  cells?: any;
  filterData?: any[];
}

export const generateColumns = (
  onColumnsSubmit: any,
  columnsExternal?: any,
  companies?: any[],
  levels?: any[],
  subcontractors?: any[],
  activities?: any[],
  areaStatuses?: any[],
  aarStatuses?: any[],
  columnOrder?: string[]
): any => {
  const getShowValue = (field: string) => {
    if (columnsExternal && Array.isArray(columnsExternal)) {
      const col = columnsExternal.find((c: any) => c.field === field);
      return col ? col.show : true;
    }
    return true;
  };

  const columnsMap: Record<string, any> = {
    companyName: {
      title: "Company",
      field: "companyName",
      show: getShowValue("companyName"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenuCheckbox={true}
          rowData={companies?.map((c) => c.name) || []}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 250,
    },
    levelName: {
      title: "Level",
      field: "levelName",
      show: getShowValue("levelName"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenuCheckbox={true}
          rowData={levels || []}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 100,
    },
    subcontractorName: {
      title: "Subcontractor",
      field: "subcontractorName",
      show: getShowValue("subcontractorName"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenuCheckbox={true}
          rowData={subcontractors?.map((s) => s.name) || []}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 200,
    },
    activityName: {
      title: "Activity",
      field: "activityName",
      show: getShowValue("activityName"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenuCheckbox={true}
          rowData={activities?.map((a) => a.value) || []}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 250,
    },
    areaName: {
      title: "Area Name",
      field: "areaName",
      show: getShowValue("areaName"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 120,
    },
    aarNumber: {
      title: "AAR",
      field: "aarNumber",
      show: getShowValue("aarNumber"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 150,
    },
    wirNumber: {
      title: "WIR Number",
      field: "wirNumber",
      show: getShowValue("wirNumber"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 150,
    },
    areaStatus: {
      title: "Area Status",
      field: "areaStatus",
      show: getShowValue("areaStatus"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenuCheckbox={true}
          rowData={areaStatuses?.map((s) => s.label) || []}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 120,
    },
    aarStatus: {
      title: "AAR Status",
      field: "aarStatus",
      show: getShowValue("aarStatus"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenuCheckbox={true}
          rowData={aarStatuses?.map((s) => s.label) || []}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      width: 120,
    },
    assignmentDate: {
      title: "Assignment Date",
      field: "assignmentDate",
      show: getShowValue("assignmentDate"),
      width: 180,
      filter: "date" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      cells: {
        data: (props: any) => {
          const date = props.dataItem?.assignmentDate;
          if (!date) return <td></td>;
          const d = new Date(date);
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = d.getFullYear();
          let hours = d.getHours();
          const minutes = String(d.getMinutes()).padStart(2, "0");
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12;
          const formatted = `${day}-${month}-${year} ${String(hours).padStart(
            2,
            "0"
          )}:${minutes} ${ampm}`;
          return <td>{formatted}</td>;
        },
      },
    },
    startDate: {
      title: "Start Date",
      field: "startDate",
      show: getShowValue("startDate"),
      width: 180,
      filter: "date" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      cells: {
        data: (props: any) => {
          const date = props.dataItem?.startDate;
          if (!date) return <td></td>;
          const d = new Date(date);
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = d.getFullYear();
          const formatted = `${day}-${month}-${year}`;
          return <td>{formatted}</td>;
        },
      },
    },
    endDate: {
      title: "End Date",
      field: "endDate",
      show: getShowValue("endDate"),
      width: 180,
      filter: "date" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
      cells: {
        data: (props: any) => {
          const date = props.dataItem?.endDate;
          if (!date) return <td></td>;
          const d = new Date(date);
          const day = String(d.getDate()).padStart(2, "0");
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const year = d.getFullYear();
          const formatted = `${day}-${month}-${year}`;
          return <td>{formatted}</td>;
        },
      },
    },
    assignedBy: {
      title: "Assigned By",
      field: "assignedBy",
      width: 200,
      show: getShowValue("assignedBy"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
    },
    remarks: {
      title: "Remarks",
      field: "remarks",
      width: 250,
      show: getShowValue("remarks"),
      filter: "text" as const,
      columnMenu: (props: any) => (
        <ShowColumnsFilter
          {...props}
          columns={Object.values(columnsMap) || columnsExternal}
          onColumnsSubmit={onColumnsSubmit}
          showColumnMenu={true}
        />
      ),
      minResizableWidth: MIN_RESIZABLE_WIDTH_KENDO_TABLE,
    },
  };

  const defaultOrder = [
    "companyName",
    "levelName",
    "subcontractorName",
    "activityName",
    "areaName",
    "aarNumber",
    "wirNumber",
    "areaStatus",
    "aarStatus",
    "assignmentDate",
    "startDate",
    "endDate",
    "assignedBy",
    "remarks",
  ];

  const order =
    columnOrder && columnOrder.length > 0 ? columnOrder : defaultOrder;
  const columns = order
    .map((field) => columnsMap[field])
    .filter((col) => col !== undefined);

  return columns;
};
